import React from "react";
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    TextField
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

interface ScheduleFormProps {
    isScheduled: boolean;
    selectedFrequency: string;
    handleFrequencyChange: (e: any) => void;
    scheduleOptions: any;
    isOtherFrequency: () => boolean;
    selectedDate: any;
    handleDateChange: (newDate: any) => void;
    isWeekly: () => boolean;
    isHourly: () => boolean;
    selectedDay: string;
    handleDayChange: (e: any) => void;
    selectedHour: string;
    selectedMinute: string;
    selectedAmPm: string;
    handleHourChange: (e: any) => void;
    handleMinuteChange: (e: any) => void;
    handleAmPmChange: (e: any) => void;
    // New props for hourly frequency
    hourlyInterval: string;
    hourlyMinute: string;
    handleHourlyIntervalChange: (e: any) => void;
    handleHourlyMinuteChange: (e: any) => void;
}

const ScheduleForm: React.FC<ScheduleFormProps> = ({
    isScheduled,
    selectedFrequency,
    handleFrequencyChange,
    scheduleOptions,
    isOtherFrequency,
    selectedDate,
    handleDateChange,
    isWeekly,
    isHourly,
    selectedDay,
    handleDayChange,
    selectedHour,
    selectedMinute,
    selectedAmPm,
    handleHourChange,
    handleMinuteChange,
    handleAmPmChange,
    hourlyInterval,
    hourlyMinute,
    handleHourlyIntervalChange,
    handleHourlyMinuteChange,
}) => {
    return (
        <>
            {isScheduled && (
                <>
                    {/* Frequency Dropdown */}
                    <FormControl fullWidth required>
                        <InputLabel>Frequency</InputLabel>
                        <Select
                            value={selectedFrequency || ""}
                            onChange={handleFrequencyChange}
                            label="Frequency"
                            disabled={!isScheduled}
                            sx={{
                                borderRadius: 2,
                                mb: 3,
                                height: "50px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "none", // Black border like TextField
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    //borderColor: "black",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "black",
                                },
                            }}
                        >
                            {scheduleOptions?.frequency?.map((option: any) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Date Input (Visible for all frequencies except Weekly/Hourly) */}
                    {isOtherFrequency() && (
                        <Box>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="On Date"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    disablePast
                                    sx={{
                                        width: "100%",
                                        mb: 3,
                                        "& .MuiOutlinedInput-root": {
                                            "& .MuiInputAdornment-root": {
                                                "& button": {
                                                    outline: "none", // Remove the focus outline
                                                    border: "none", // Ensure no border is applied
                                                },
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                    )}

                    {/* On Day Dropdown (Weekly Frequency) */}
                    {isWeekly() && (
                        <FormControl fullWidth required>
                            <InputLabel>On Day</InputLabel>
                            <Select
                                value={selectedDay || ""}
                                onChange={handleDayChange}
                                label="On Day"
                            >
                                {scheduleOptions?.Days?.map((day: any) => (
                                    <MenuItem key={day.id} value={day.id}>
                                        {day.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Hourly Time Display (Hourly Frequency) */}
                    {isHourly() && (
                        <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'center' }}>
                            {/* Hour Interval TextField */}
                            <TextField
                                label="Hour"
                                value={hourlyInterval || ""}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "");
                                    const numValue = parseInt(value);
                                    if (value === "" || (numValue >= 1 && numValue <= 24)) {
                                        handleHourlyIntervalChange({ ...e, target: { ...e.target, value } });
                                    }
                                }}
                                type="text"
                                inputProps={{
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                    min: 1,
                                    max: 24,
                                    step: 1,
                                    maxLength: 2
                                }}
                                required
                                sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                        height: "50px",
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "rgba(0, 0, 0, 0.23)",
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "black",
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "black",
                                        },
                                    },
                                }}
                            />

                            {/* Minutes TextField */}
                            <TextField
                                label="Minutes"
                                value={hourlyMinute || ""}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "");
                                    const numValue = parseInt(value);
                                    if (value === "" || (numValue >= 0 && numValue <= 59)) {
                                        handleHourlyMinuteChange({ ...e, target: { ...e.target, value } });
                                    }
                                }}
                                type="text"
                                inputProps={{
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                    min: 0,
                                    max: 59,
                                    step: 1,
                                    maxLength: 2
                                }}
                                required
                                sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                        height: "50px",
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "rgba(0, 0, 0, 0.23)",
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "black",
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "black",
                                        },
                                    },
                                }}
                            />

                            {/* AM/PM Dropdown */}
                            <FormControl
                                required
                            >
                                <InputLabel>AM/PM</InputLabel>
                                <Select
                                    value={selectedAmPm}
                                    onChange={handleAmPmChange}
                                    label="AM/PM"
                                    sx={{
                                        borderRadius: 2,
                                        height: "48px",
                                        width: "107px",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            //borderColor: "black", 
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "black",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "black",
                                        },
                                    }}
                                >
                                    <MenuItem value=""></MenuItem>

                                    {["AM", "PM"].map((amPm) => {

                                        if (
                                            amPm === "AM" || amPm === "PM"
                                        ) {
                                            return (
                                                <MenuItem key={amPm} value={amPm}>
                                                    {amPm}
                                                </MenuItem>
                                            )
                                        }

                                        return null;
                                    })}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {/* Time Inputs - Hour, Minute, AM/PM (Not for Hourly) */}
                    {!isHourly() && (
                        <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'center' }}>
                            {/* Hour TextField */}
                            <TextField
                                label="Hour"
                                value={selectedHour || ""}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "");
                                    const numValue = parseInt(value);
                                    if (value === "" || (numValue >= 1 && numValue <= 12)) {
                                        handleHourChange({ ...e, target: { ...e.target, value } });
                                    }
                                }}
                                type="text"
                                inputProps={{
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                    min: 1,
                                    max: 12,
                                    step: 1,
                                    maxLength: 2
                                }}
                                required
                                sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                        height: "50px",
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "rgba(0, 0, 0, 0.23)",
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "black",
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "black",
                                        },
                                    },
                                }}
                            />

                            {/* Minutes TextField */}
                            <TextField
                                label="Minutes"
                                value={selectedMinute || ""}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "");
                                    const numValue = parseInt(value);
                                    if (value === "" || (numValue >= 0 && numValue <= 59)) {
                                        handleMinuteChange({ ...e, target: { ...e.target, value } });
                                    }
                                }}
                                type="text"
                                inputProps={{
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                    min: 0,
                                    max: 59,
                                    step: 1,
                                    maxLength: 2
                                }}
                                required
                                sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                        height: "50px",
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "rgba(0, 0, 0, 0.23)",
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "black",
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: "black",
                                        },
                                    },
                                }}
                            />

                            {/* AM/PM Dropdown */}
                            <FormControl
                                required
                            >
                                <InputLabel>AM/PM</InputLabel>
                                <Select
                                    value={selectedAmPm}
                                    onChange={handleAmPmChange}
                                    label="AM/PM"
                                    sx={{
                                        borderRadius: 2,
                                        height: "48px",
                                        width: "107px",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            //borderColor: "black", 
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "black",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "black",
                                        },
                                    }}
                                >
                                    <MenuItem value=""></MenuItem>

                                    {["AM", "PM"].map((amPm) => {

                                        if (
                                            amPm === "AM" || amPm === "PM"
                                        ) {
                                            return (
                                                <MenuItem key={amPm} value={amPm}>
                                                    {amPm}
                                                </MenuItem>
                                            )
                                        }

                                        return null;
                                    })}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </>
            )}
        </>
    );
};

export default ScheduleForm;